import { makeAutoObservable } from 'mobx';

// Import tutorial step configurations
import landingSteps from '../configs/tutorials/landing.json';
import searchResultsSteps from '../configs/tutorials/search-results.json';
import evidenceSteps from '../configs/tutorials/evidence.json';
import llmSteps from '../configs/tutorials/llm.json';
import aimodalSteps from '../configs/tutorials/aimodal.json';

const TUTORIAL_STATE_KEY = 'plex-tutorial-state';

const CONTEXT_STEPS = {
  landing: landingSteps,
  results: searchResultsSteps,
  evidence: evidenceSteps,
  llm: llmSteps,
  aimodal: aimodalSteps,
};

class TutorialStore {
  // Joyride control — TutorialRunner observes these
  run = false;
  stepIndex = 0;
  currentContext = null;
  steps = [];

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // ── localStorage state ──────────────────────────────────────

  _getState() {
    try {
      return JSON.parse(localStorage.getItem(TUTORIAL_STATE_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }

  _setState(state) {
    try {
      localStorage.setItem(TUTORIAL_STATE_KEY, JSON.stringify(state));
    } catch (e) {}
  }

  getContextState(context) {
    return this._getState()[context] || { completed: false, skipped: false, progress: null };
  }

  setContextState(context, updates) {
    const all = this._getState();
    all[context] = { ...this.getContextState(context), ...updates, timestamp: Date.now() };
    this._setState(all);
  }

  hasCompletedContext(context) { return this.getContextState(context).completed === true; }
  hasSkippedContext(context)   { return this.getContextState(context).skipped === true; }
  shouldShowContext(context)   { const s = this.getContextState(context); return !s.completed && !s.skipped; }

  getProgress(context) { return this.getContextState(context).progress; }
  hasProgress(context) { const p = this.getProgress(context); return p !== null && p?.step > 0; }
  saveProgress(context, step) { this.setContextState(context, { progress: { step, timestamp: Date.now() } }); }
  clearProgress(context)      { this.setContextState(context, { progress: null }); }

  markCompleted(context) { this.setContextState(context, { completed: true, skipped: false, progress: null }); }
  markSkipped(context)   { this.setContextState(context, { skipped: true, progress: null }); }

  resetContext(context) {
    const all = this._getState();
    delete all[context];
    if (Object.keys(all).length === 0) localStorage.removeItem(TUTORIAL_STATE_KEY);
    else this._setState(all);
  }

  resetAllTutorials() { localStorage.removeItem(TUTORIAL_STATE_KEY); }

  // ── Tour control ────────────────────────────────────────────

  get isActive() { return this.run; }

  startContextTutorial(context) {
    const steps = CONTEXT_STEPS[context] || landingSteps;
    // Interactive tutorials (landing, evidence, aimodal) always restart from step 0
    // because they require live UI state that won't exist on a fresh page load.
    const resumable = ['results', 'llm'].includes(context);
    const progress = resumable ? this.getProgress(context) : null;
    this.steps = steps;
    this.currentContext = context;
    this.stepIndex = progress?.step > 0 ? progress.step : 0;
    this.run = true;
  }

  exitTutorial() {
    this.run = false;
  }

  // Called by TutorialRunner on step change
  onStepChange(index) {
    this.stepIndex = index;
    if (this.currentContext) {
      this.saveProgress(this.currentContext, index);
    }
  }

  // Called by TutorialRunner on finish/skip
  onTourEnd(finished) {
    this.run = false;
    if (finished) {
      this.markCompleted(this.currentContext);
    } else {
      this.markSkipped(this.currentContext);
    }
  }
}

export default TutorialStore;
